import React, { useEffect, useMemo, useRef, useState } from "react";
import { jsPDF } from "jspdf";
import { useNavigate } from "react-router-dom";
import API_URL from "../apiConfig";

const JOURNAL_TIME_ZONE = "America/Toronto";

function parseJournalDate(value) {
  if (!value) return null;

  const text = String(value).trim();

  // Evita que una fecha YYYY-MM-DD retroceda al día anterior
  // al ser interpretada como medianoche UTC.
  const dateOnlyMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;

    return new Date(Number(year), Number(month) - 1, Number(day), 12, 0, 0);
  }

  const date = new Date(text);

  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(value) {
  const date = parseJournalDate(value);

  if (!date) return "Sin fecha";

  return new Intl.DateTimeFormat("es-CA", {
    timeZone: JOURNAL_TIME_ZONE,
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
}

function getDateInputValue(value) {
  if (!value) return "";

  const text = String(value).trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    return text;
  }

  const date = parseJournalDate(value);
  if (!date) return "";

  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: JOURNAL_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);

  const values = Object.fromEntries(
    parts.map((part) => [part.type, part.value]),
  );

  return `${values.year}-${values.month}-${values.day}`;
}

function getEntryCategory(entry) {
  const sourceType = entry?.source_type || "";
  const sourceModule = entry?.source_module || "";

  if (sourceType === "connection_wall_snapshot") {
    return "connection_wall_snapshot";
  }
  if (sourceType === "micro_mission") return "micro_mission";
  if (sourceType === "word" || sourceType === "reflection") return "word";
  if (
    sourceType === "conexion" ||
    sourceType === "prayer" ||
    sourceModule === "conexion"
  ) {
    return "conexion";
  }
  if (sourceType === "guide" || sourceModule === "guide") return "guide";

  return "all";
}

function getEntryCategoryLabel(entry) {
  const category = getEntryCategory(entry);

  if (category === "micro_mission") return "TOMA ACCIÓN";
  if (category === "word") return "La Palabra en acción";
  if (category === "connection_wall_snapshot") return "Muro de oración";
  if (category === "conexion") return "Oración";

  return "Registro ZEDRO";
}

function getBlockLabel(kind) {
  if (kind === "rule") return "Práctica del día";
  if (kind === "single_choice") return "Lo que elegiste";
  if (kind === "multi_choice") return "Lo que identificaste";
  if (kind === "binary") return "Tu respuesta";
  if (kind === "scale") return "Tu valoración";
  if (kind === "short_text" || kind === "long_text") return "Tu reflexión";
  if (kind === "complete_phrase") return "Tu frase";
  if (kind === "confirmation") return "Práctica preparada";
  if (kind === "micro_mission") return "Micro-misión";
  if (kind === "reflection" || kind === "word") return "La Palabra en acción";
  if (kind === "conexion" || kind === "prayer") return "Conexión";
  if (kind === "guide") return "Guía";

  return "Pregunta / Acción";
}

function renderFormattedText(value) {
  const text = String(value || "").trim();

  if (!text) return null;

  const paragraphs = text
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  return paragraphs.map((paragraph, paragraphIndex) => {
    const lines = paragraph.split("\n");

    return (
      <p
        key={paragraphIndex}
        style={{
          margin: paragraphIndex === 0 ? 0 : "0.95rem 0 0",
        }}
      >
        {lines.map((line, lineIndex) => {
          const parts = line.split(/(\*\*[^*]+\*\*)/g);

          return (
            <React.Fragment key={lineIndex}>
              {lineIndex > 0 && <br />}

              {parts.map((part, index) => {
                if (part.startsWith("**") && part.endsWith("**")) {
                  return (
                    <strong key={index}>{part.replaceAll("**", "")}</strong>
                  );
                }

                return <React.Fragment key={index}>{part}</React.Fragment>;
              })}
            </React.Fragment>
          );
        })}
      </p>
    );
  });
}

function renderConnectionWallSnapshot(entry) {
  const snapshot = entry?.response || {};

  const columns = [
    {
      key: "battle_plan",
      title: "Plan de Batalla",
      subtitle: "Verdades bíblicas que sostienen tu oración.",
      items: snapshot.battle_plan || [],
    },
    {
      key: "petitions",
      title: "Peticiones activas",
      subtitle: "Lo que presentaste delante de Dios.",
      items: snapshot.petitions || [],
    },
    {
      key: "prayers",
      title: "Oraciones",
      subtitle: "Palabras ordenadas desde tu proceso.",
      items: snapshot.prayers || [],
    },
    {
      key: "victories",
      title: "Victorias",
      subtitle: "Respuestas que fortalecen tu memoria.",
      items: snapshot.victories || [],
    },
  ];

  function renderSnapshotCard(card) {
    return (
      <article key={card.id} className="bitacoraWallCard">
        <div className="bitacoraWallCardTop">
          {card.category && <span>{card.category}</span>}
        </div>

        {card.title && <h4>{card.title}</h4>}

        {card.scripture_reference && (
          <p className="bitacoraWallReference">{card.scripture_reference}</p>
        )}

        {card.scripture_text && (
          <p className="bitacoraWallText">{card.scripture_text}</p>
        )}

        {card.content && <p className="bitacoraWallText">{card.content}</p>}

        {card.prayer_text && (
          <div className="bitacoraWallPrayer">
            <strong>Oración</strong>
            <p>{card.prayer_text}</p>
          </div>
        )}

        {card.answered_note && (
          <div className="bitacoraWallVictory">
            <strong>Victoria registrada</strong>
            <p>{card.answered_note}</p>
          </div>
        )}
      </article>
    );
  }

  return (
    <div className="bitacoraWallSnapshot">
      <div className="bitacoraWallSnapshotHead">
        <span>Cuarto de Guerra</span>
      </div>

      <div className="bitacoraWallGrid">
        {columns.map((column) => (
          <section
            key={column.key}
            className={`bitacoraWallColumn bitacoraWallColumn--${column.key}`}
          >
            <div className="bitacoraWallColumnHead">
              <h4>{column.title}</h4>
              <p>{column.subtitle}</p>
            </div>

            <div className="bitacoraWallCards">
              {column.items.length > 0 ? (
                column.items.map((card) => renderSnapshotCard(card))
              ) : (
                <p className="bitacoraWallEmpty">
                  Sin registros en esta sección.
                </p>
              )}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function renderResponse(response, entry) {
  if (entry?.source_type === "connection_wall_snapshot") {
    return renderConnectionWallSnapshot(entry);
  }

  if (!response || typeof response !== "object") {
    return <p className="bitacoraEmptyText">No hay respuestas registradas.</p>;
  }

  const reflectionCards = Array.isArray(response.reflection_cards) && response.reflection_cards.length
    ? response.reflection_cards
    : response.reflection_card
      ? [response.reflection_card]
      : [];

  if (
    Array.isArray(response.journal_entries) &&
    response.journal_entries.length > 0
  ) {
    return (
      <>
        {reflectionCards.map((card, index) =>
          card?.title && card?.body ? (
            <section
              key={`${card.title}-${index}`}
              className="bitacoraReflectionCard"
              aria-label="Tarjeta de recorrido"
            >
              <span>ZEDRO Academy · Mi recorrido</span>
              <h3>{card.title}</h3>
              <p>{card.body}</p>
            </section>
          ) : null,
        )}
        <div className="bitacoraResponseGrid">
          {response.journal_entries.map((item, index) => {
            const label = getBlockLabel(item.kind);

            return (
              <div
                key={item.block_id || index}
                className={`bitacoraResponseCard ${
                  item.kind === "rule" ? "is-rule" : ""
                }`}
              >
                <div className="bitacoraResponseLabel">{label}</div>

                <div className="bitacoraResponseQuestion">
                  {renderFormattedText(item.question)}
                </div>

                <div className="bitacoraResponseAnswer">
                  {renderFormattedText(item.answer)}
                </div>
              </div>
            );
          })}
        </div>
      </>
    );
  }

  return (
    <p className="bitacoraEmptyText">No hay respuestas visibles registradas.</p>
  );
}

function cleanPdfText(value) {
  return String(value || "")
    .replace(/\*\*/g, "")
    .replace(/\[\[SCRIPTURE_REFERENCE\]\]/g, "")
    .replace(/\[\[BULLET:dot\]\]/g, "• ")
    .replace(/\[\[QUOTE\]\]/g, "")
    .replace(/\[\[ICON:[^\]]+\]\]/g, "")
    .replace(/\r\n/g, "\n")
    .trim();
}

function wrapCanvasText(ctx, text, maxWidth) {
  const paragraphs = cleanPdfText(text).split(/\n+/).filter(Boolean);
  const lines = [];

  paragraphs.forEach((paragraph, paragraphIndex) => {
    const words = paragraph.split(/\s+/).filter(Boolean);
    let line = "";
    words.forEach((word) => {
      const candidate = line ? `${line} ${word}` : word;
      if (line && ctx.measureText(candidate).width > maxWidth) {
        lines.push(line);
        line = word;
      } else {
        line = candidate;
      }
    });
    if (line) lines.push(line);
    if (paragraphIndex < paragraphs.length - 1) lines.push("");
  });

  return lines;
}

function buildPdfFileName(entry) {
  const category = getEntryCategory(entry);
  const day = entry?.lesson_day_number
    ? `dia-${entry.lesson_day_number}`
    : "registro";

  const type =
    category === "word"
      ? "palabra-en-accion"
      : category === "micro_mission"
        ? "toma-accion"
        : category === "conexion"
          ? "conexion-zedro"
          : category;

  return `ZEDRO-Academy-${type}-${day}.pdf`;
}

const JOURNAL_PAGE_SIZE = 9;

function slugifyModuleLabel(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function humanizeModuleValue(value) {
  return String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function getEntryModule(entry) {
  const sourceModule = String(entry?.source_module || "").trim();
  const explicitLabel = String(
    entry?.module_title ||
      entry?.module_name ||
      entry?.learning_line_title ||
      entry?.learning_area_title ||
      "",
  ).trim();

  const searchable = [
    sourceModule,
    explicitLabel,
    entry?.system_title,
    entry?.title,
    entry?.source_type,
  ]
    .filter(Boolean)
    .join(" ")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

  if (
    entry?.source_type === "connection_wall_snapshot" ||
    /conexion|prayer|oracion|muro/.test(searchable)
  ) {
    return { id: "conexion-zedro", label: "Conexión ZEDRO" };
  }

  if (/liderazgo|gobierno|leadership|governance/.test(searchable)) {
    return { id: "liderazgo-gobierno", label: "Liderazgo y gobierno" };
  }

  if (/gestion empresarial|empresarial|empresa|business/.test(searchable)) {
    return { id: "gestion-empresarial", label: "Gestión empresarial" };
  }

  if (explicitLabel) {
    return {
      id: slugifyModuleLabel(explicitLabel) || "modulo-zedro",
      label: explicitLabel,
    };
  }

  const genericSourceModules = new Set([
    "",
    "guide",
    "word",
    "reflection",
    "micro_mission",
    "micro-mission",
    "prayer",
    "conexion",
    "system",
    "cimientos",
    "core",
  ]);

  const normalizedSourceModule = sourceModule.toLowerCase();

  if (sourceModule && !genericSourceModules.has(normalizedSourceModule)) {
    const label = humanizeModuleValue(sourceModule);

    return {
      id: slugifyModuleLabel(sourceModule) || "modulo-zedro",
      label,
    };
  }

  return { id: "cimientos-zedro", label: "Cimientos ZEDRO" };
}

function getPaginationItems(currentPage, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages = new Set([
    1,
    totalPages,
    currentPage - 1,
    currentPage,
    currentPage + 1,
  ]);

  const ordered = [...pages]
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((a, b) => a - b);

  const items = [];

  ordered.forEach((page, index) => {
    const previousPage = ordered[index - 1];

    if (index > 0 && page - previousPage > 1) {
      items.push(`ellipsis-${previousPage}-${page}`);
    }

    items.push(page);
  });

  return items;
}

export default function Bitacora() {
  const navigate = useNavigate();
  const resultsRef = useRef(null);

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeModule, setActiveModule] = useState("all");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedEntryId, setSelectedEntryId] = useState(null);
  const [readerOpen, setReaderOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [noteMessage, setNoteMessage] = useState("");

  useEffect(() => {
    loadJournal();
  }, []);

  useEffect(() => {
    if (!readerOpen) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    function handleEscape(event) {
      if (event.key === "Escape") {
        setReaderOpen(false);
      }
    }

    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [readerOpen]);

  async function loadJournal() {
    try {
      const session = JSON.parse(localStorage.getItem("zedro_session"));

      const res = await fetch(`${API_URL}/api/journal`, {
        headers: {
          Authorization: `Bearer ${session?.token}`,
        },
      });

      const json = await res.json();

      if (json.ok) {
        setEntries(json.entries || []);
      }
    } catch (err) {
      console.error("[BITACORA_LOAD_ERROR]", err);
    } finally {
      setLoading(false);
    }
  }

  const moduleTabs = useMemo(() => {
    const modules = new Map();

    entries.forEach((entry) => {
      const module = getEntryModule(entry);
      const current = modules.get(module.id);

      modules.set(module.id, {
        ...module,
        count: (current?.count || 0) + 1,
      });
    });

    const preferredOrder = {
      "cimientos-zedro": 10,
      "liderazgo-gobierno": 20,
      "gestion-empresarial": 30,
      "conexion-zedro": 40,
    };

    const orderedModules = [...modules.values()].sort((a, b) => {
      const orderA = preferredOrder[a.id] ?? 100;
      const orderB = preferredOrder[b.id] ?? 100;

      if (orderA !== orderB) return orderA - orderB;

      return a.label.localeCompare(b.label, "es", { sensitivity: "base" });
    });

    return [
      { id: "all", label: "Todos", count: entries.length },
      ...orderedModules,
    ];
  }, [entries]);

  useEffect(() => {
    if (
      activeModule !== "all" &&
      !moduleTabs.some((module) => module.id === activeModule)
    ) {
      setActiveModule("all");
    }
  }, [activeModule, moduleTabs]);

  const filteredEntries = useMemo(() => {
    return entries.filter((entry) => {
      const query = search.trim().toLowerCase();
      const module = getEntryModule(entry);
      const searchableText =
        `${module.label} ${JSON.stringify(entry)}`.toLowerCase();
      const entryDate = getDateInputValue(entry.entry_date || entry.created_at);

      const matchesModule =
        activeModule === "all" || module.id === activeModule;
      const matchesSearch = !query || searchableText.includes(query);
      const matchesDateFrom = !dateFrom || entryDate >= dateFrom;
      const matchesDateTo = !dateTo || entryDate <= dateTo;

      return matchesModule && matchesSearch && matchesDateFrom && matchesDateTo;
    });
  }, [entries, activeModule, search, dateFrom, dateTo]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredEntries.length / JOURNAL_PAGE_SIZE),
  );

  const paginatedEntries = useMemo(() => {
    const startIndex = (currentPage - 1) * JOURNAL_PAGE_SIZE;

    return filteredEntries.slice(startIndex, startIndex + JOURNAL_PAGE_SIZE);
  }, [filteredEntries, currentPage]);

  const paginationItems = useMemo(
    () => getPaginationItems(currentPage, totalPages),
    [currentPage, totalPages],
  );

  useEffect(() => {
    setCurrentPage(1);
    setReaderOpen(false);
    setSelectedEntryId(null);
  }, [activeModule, search, dateFrom, dateTo, entries.length]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    if (!selectedEntryId) return;

    const selectedStillExists = filteredEntries.some(
      (entry) => entry.id === selectedEntryId,
    );

    if (!selectedStillExists) {
      setSelectedEntryId(null);
      setReaderOpen(false);
    }
  }, [filteredEntries, selectedEntryId]);

  const selectedEntry = filteredEntries.find(
    (entry) => entry.id === selectedEntryId,
  );

  const isConnectionWallSnapshot =
    selectedEntry?.source_type === "connection_wall_snapshot";

  const isConnectionEntry =
    isConnectionWallSnapshot || getEntryCategory(selectedEntry) === "conexion";

  useEffect(() => {
    setNoteDraft(isConnectionEntry ? "" : selectedEntry?.personal_note || "");
    setNoteMessage("");
  }, [selectedEntry?.id, selectedEntry?.personal_note, isConnectionEntry]);

  function getDisplayTitle(entry) {
    if (!entry) return "";

    const category = getEntryCategory(entry);

    if (category === "connection_wall_snapshot") {
      return "Muro de Oración";
    }

    if (
      (category === "word" || category === "micro_mission") &&
      entry.lesson_day_number &&
      entry.lesson_title
    ) {
      return `Día ${entry.lesson_day_number}: ${entry.lesson_title}`;
    }

    return entry.title || entry.lesson_title || "Registro ZEDRO";
  }

  const hasActiveFilters =
    activeModule !== "all" || Boolean(search || dateFrom || dateTo);

  function handleModuleChange(moduleId) {
    setActiveModule(moduleId);
  }

  function clearFilters() {
    setActiveModule("all");
    setSearch("");
    setDateFrom("");
    setDateTo("");
    setCurrentPage(1);
    setReaderOpen(false);
  }

  function handleSelectEntry(entryId) {
    setSelectedEntryId(entryId);
    setReaderOpen(true);
  }

  function closeReader() {
    setReaderOpen(false);
  }

  function changePage(nextPage) {
    const safePage = Math.min(Math.max(nextPage, 1), totalPages);

    setCurrentPage(safePage);
    setSelectedEntryId(null);
    setReaderOpen(false);

    window.requestAnimationFrame(() => {
      resultsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }

  async function savePersonalNote() {
    if (!selectedEntry?.id || isConnectionEntry) return;

    try {
      setSavingNote(true);
      setNoteMessage("");

      const session = JSON.parse(localStorage.getItem("zedro_session"));

      const res = await fetch(
        `${API_URL}/api/journal/${selectedEntry.id}/note`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.token}`,
          },
          body: JSON.stringify({
            note: noteDraft,
          }),
        },
      );

      const json = await res.json();

      if (!res.ok || !json.ok) {
        throw new Error(json.error || "No se pudo guardar la nota");
      }

      setEntries((current) =>
        current.map((entry) =>
          entry.id === selectedEntry.id
            ? {
                ...entry,
                personal_note: json.note.note,
                personal_note_updated_at: json.note.updated_at,
              }
            : entry,
        ),
      );

      setNoteMessage("Nota guardada.");
    } catch (err) {
      console.error("[SAVE_PERSONAL_NOTE_ERROR]", err);
      setNoteMessage("No se pudo guardar la nota.");
    } finally {
      setSavingNote(false);
    }
  }

  function downloadConnectionWallSnapshotPdf(entry) {
    if (!entry) return;

    const snapshot = entry.response || {};

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "letter",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const margin = 36;
    const wallX = margin;
    const wallY = 96;
    const wallWidth = pageWidth - margin * 2;
    const wallHeight = pageHeight - 135;

    const dateLabel = formatDate(entry.entry_date || entry.created_at);

    function cleanText(value) {
      return String(value || "")
        .replace(/\*\*/g, "")
        .replace(/\r\n/g, "\n")
        .trim();
    }

    function addWrapped(text, x, y, width, options = {}) {
      const {
        fontSize = 8,
        fontStyle = "normal",
        color = [15, 47, 70],
        lineHeight = 10,
        maxLines = 8,
      } = options;

      const clean = cleanText(text);
      if (!clean) return y;

      doc.setFont("helvetica", fontStyle);
      doc.setFontSize(fontSize);
      doc.setTextColor(...color);

      const lines = doc.splitTextToSize(clean, width).slice(0, maxLines);

      lines.forEach((line) => {
        doc.text(line, x, y);
        y += lineHeight;
      });

      return y;
    }

    function drawRoundedRect(x, y, w, h, fill, stroke) {
      doc.setFillColor(...fill);
      doc.setDrawColor(...stroke);
      doc.roundedRect(x, y, w, h, 16, 16, "FD");
    }

    function drawCard(card, x, y, w) {
      const cardHeight = 118;

      drawRoundedRect(x, y, w, cardHeight, [255, 255, 255], [226, 230, 232]);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(6.5);
      doc.setTextColor(155, 117, 39);

      if (card.category) {
        doc.text(String(card.category).toUpperCase(), x + 12, y + 18);
      }

      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(15, 47, 70);

      let cursorY = y + 36;
      const titleLines = doc
        .splitTextToSize(cleanText(card.title), w - 24)
        .slice(0, 2);

      titleLines.forEach((line) => {
        doc.text(line, x + 12, cursorY);
        cursorY += 10;
      });

      if (card.scripture_reference) {
        cursorY += 3;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7.5);
        doc.setTextColor(138, 105, 44);
        doc.text(cleanText(card.scripture_reference), x + 12, cursorY);
        cursorY += 12;
      }

      const body =
        card.scripture_text ||
        card.content ||
        card.prayer_text ||
        card.answered_note ||
        "";

      addWrapped(body, x + 12, cursorY, w - 24, {
        fontSize: 7.4,
        color: [34, 56, 77],
        lineHeight: 9,
        maxLines: 6,
      });

      return cardHeight + 10;
    }

    function drawColumn(column, x, y, w, h, fill) {
      drawRoundedRect(x, y, w, h, fill, [214, 178, 94]);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(15, 47, 70);
      doc.text(column.title, x + 12, y + 22);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.4);
      doc.setTextColor(66, 86, 102);

      const subtitleLines = doc
        .splitTextToSize(column.subtitle, w - 24)
        .slice(0, 2);

      let subtitleY = y + 36;
      subtitleLines.forEach((line) => {
        doc.text(line, x + 12, subtitleY);
        subtitleY += 9;
      });

      let cardY = y + 60;

      if (!column.items.length) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(66, 86, 102);
        doc.text("Sin registros en esta sección.", x + 12, cardY);
        return;
      }

      column.items.slice(0, 3).forEach((card) => {
        if (cardY + 118 < y + h - 12) {
          cardY += drawCard(card, x + 10, cardY, w - 20);
        }
      });

      if (column.items.length > 3) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.setTextColor(155, 117, 39);
        doc.text(
          `+ ${column.items.length - 3} registros más`,
          x + 12,
          y + h - 14,
        );
      }
    }

    // Fondo general
    doc.setFillColor(248, 249, 248);
    doc.rect(0, 0, pageWidth, pageHeight, "F");

    // Encabezado
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(155, 117, 39);
    doc.text("ZEDRO ACADEMY", margin, 38);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(102, 119, 132);
    doc.text("Formación con raíz eterna", margin, 52);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(22);
    doc.setTextColor(15, 47, 70);
    doc.text("Muro de Oración", margin, 78);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(102, 119, 132);
    doc.text(`Fecha: ${dateLabel}`, pageWidth - margin, 42, { align: "right" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(66, 86, 102);
    doc.text(
      "Seguimiento diario de registros bíblicos, peticiones, oraciones y victorias.",
      margin,
      92,
    );

    // Muro
    doc.setFillColor(12, 38, 58);
    doc.setDrawColor(204, 163, 82);
    doc.roundedRect(wallX, wallY, wallWidth, wallHeight, 22, 22, "FD");

    doc.setFillColor(255, 248, 232);
    doc.setDrawColor(204, 163, 82);
    doc.roundedRect(wallX + 18, wallY + 18, 104, 24, 12, 12, "FD");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(155, 117, 39);
    doc.text("CONEXIÓN ZEDRO", wallX + 32, wallY + 34);

    const columns = [
      {
        title: "Plan de Batalla",
        subtitle: "Verdades bíblicas que sostienen tu oración.",
        items: snapshot.battle_plan || [],
        fill: [242, 234, 215],
      },
      {
        title: "Peticiones activas",
        subtitle: "Lo que presentaste delante de Dios.",
        items: snapshot.petitions || [],
        fill: [223, 234, 241],
      },
      {
        title: "Oraciones",
        subtitle: "Palabras ordenadas desde tu proceso.",
        items: snapshot.prayers || [],
        fill: [236, 230, 216],
      },
      {
        title: "Victorias",
        subtitle: "Respuestas que fortalecen tu memoria.",
        items: snapshot.victories || [],
        fill: [222, 235, 231],
      },
    ];

    const gap = 12;
    const columnW = (wallWidth - 36 - gap * 3) / 4;
    const columnY = wallY + 58;
    const columnH = wallHeight - 76;

    columns.forEach((column, index) => {
      const x = wallX + 18 + index * (columnW + gap);
      drawColumn(column, x, columnY, columnW, columnH, column.fill);
    });

    // Footer
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(120, 132, 142);
    doc.text(
      "ZEDRO Academy · Formación con raíz eterna",
      margin,
      pageHeight - 24,
    );

    doc.save("ZEDRO-Academy-muro-de-oracion.pdf");
  }

  function downloadReflectionCard(format = "vertical", selectedCard = null) {
    const card = selectedCard || selectedEntry?.response?.reflection_card;
    if (!card?.title || !card?.body) return;

    const isWallpaper = format === "wallpaper";
    const width = isWallpaper ? 1440 : 1080;
    const height = isWallpaper ? 3200 : 1350;
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const scale = width / 1080;
    const padding = 92 * scale;
    const contentWidth = width - padding * 2;

    ctx.fillStyle = "#264763";
    ctx.fillRect(0, 0, width, height);

    const glow = ctx.createRadialGradient(
      width * 0.82,
      height * 0.12,
      0,
      width * 0.82,
      height * 0.12,
      width * 0.72,
    );
    glow.addColorStop(0, "rgba(210,174,93,0.22)");
    glow.addColorStop(1, "rgba(38,71,99,0)");
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, width, height);

    ctx.fillStyle = "#D5B46A";
    ctx.font = `700 ${24 * scale}px Arial, sans-serif`;
    ctx.fillText("ZEDRO ACADEMY", padding, 120 * scale);

    ctx.fillStyle = "rgba(255,255,255,0.72)";
    ctx.font = `${19 * scale}px Arial, sans-serif`;
    ctx.fillText("MI RECORRIDO", padding, 168 * scale);

    ctx.fillStyle = "#FFFFFF";
    ctx.font = `600 ${52 * scale}px Georgia, serif`;
    const titleLines = wrapCanvasText(ctx, card.title, contentWidth);
    let y = 275 * scale;
    titleLines.forEach((line) => {
      ctx.fillText(line, padding, y);
      y += 66 * scale;
    });

    y += 34 * scale;
    ctx.fillStyle = "#D5B46A";
    ctx.fillRect(padding, y, 86 * scale, 3 * scale);
    y += 70 * scale;

    ctx.fillStyle = "rgba(255,255,255,0.94)";
    let bodyFontSize = (isWallpaper ? 31 : 29) * scale;
    const minBodyFontSize = (isWallpaper ? 22 : 18) * scale;
    let bodyLines = [];
    let lineHeight = 0;
    const bodyBottom = height - 150 * scale;

    do {
      ctx.font = `${bodyFontSize}px Arial, sans-serif`;
      bodyLines = wrapCanvasText(ctx, card.body, contentWidth);
      lineHeight = bodyFontSize * 1.52;
      const estimatedHeight = bodyLines.reduce(
        (total, line) => total + (line ? lineHeight : lineHeight * 0.65),
        0,
      );
      if (y + estimatedHeight <= bodyBottom || bodyFontSize <= minBodyFontSize)
        break;
      bodyFontSize -= 1.5 * scale;
    } while (bodyFontSize >= minBodyFontSize);

    ctx.font = `${bodyFontSize}px Arial, sans-serif`;
    bodyLines.forEach((line) => {
      if (y < bodyBottom) ctx.fillText(line, padding, y);
      y += line ? lineHeight : lineHeight * 0.65;
    });

    ctx.fillStyle = "rgba(255,255,255,0.58)";
    ctx.font = `${17 * scale}px Arial, sans-serif`;
    ctx.fillText(
      "Formación con fundamento cristiano",
      padding,
      height - 72 * scale,
    );

    const link = document.createElement("a");
    const safeTitle = cleanPdfText(card.title)
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .toLowerCase();
    link.download = `zedro-${safeTitle || "mi-recorrido"}-${format}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  }

  function downloadSelectedEntryPdf() {
    if (!selectedEntry) return;

    if (selectedEntry.source_type === "connection_wall_snapshot") {
      downloadConnectionWallSnapshotPdf(selectedEntry);
      return;
    }

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "pt",
      format: "letter",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const marginX = 56;
    const marginTop = 56;
    const marginBottom = 58;
    const contentWidth = pageWidth - marginX * 2;

    let y = marginTop;

    function footer() {
      const pageNumber = doc.internal.getNumberOfPages();

      doc.setDrawColor(225, 228, 230);
      doc.line(marginX, pageHeight - 42, pageWidth - marginX, pageHeight - 42);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(120, 132, 142);
      doc.text(
        "ZEDRO Academy · Formación con raíz eterna",
        marginX,
        pageHeight - 26,
      );
      doc.text(String(pageNumber), pageWidth - marginX, pageHeight - 26, {
        align: "right",
      });
    }

    function addPageIfNeeded(extraSpace = 40) {
      if (y + extraSpace > pageHeight - marginBottom) {
        footer();
        doc.addPage();
        y = marginTop;
      }
    }

    function addWrappedText(text, options = {}) {
      const {
        fontSize = 10.5,
        fontStyle = "normal",
        color = [34, 56, 77],
        lineHeight = 15,
        spacingAfter = 10,
        indent = 0,
      } = options;

      const cleanText = cleanPdfText(text);
      if (!cleanText) return;

      const paragraphs = cleanText.split(/\n\s*\n/).filter(Boolean);

      paragraphs.forEach((paragraph, paragraphIndex) => {
        const lines = doc.splitTextToSize(
          paragraph.trim(),
          contentWidth - indent,
        );

        lines.forEach((line) => {
          addPageIfNeeded(lineHeight + 6);

          doc.setFont("helvetica", fontStyle);
          doc.setFontSize(fontSize);
          doc.setTextColor(...color);

          doc.text(line, marginX + indent, y);
          y += lineHeight;
        });

        if (paragraphIndex < paragraphs.length - 1) {
          y += 7;
        }
      });

      y += spacingAfter;
    }

    function addSectionTitle(title) {
      addPageIfNeeded(36);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(155, 117, 39);
      doc.text(String(title || "").toUpperCase(), marginX, y);

      y += 18;

      doc.setDrawColor(232, 218, 188);
      doc.line(marginX, y, pageWidth - marginX, y);

      y += 18;
    }

    const categoryLabel = getEntryCategoryLabel(selectedEntry);
    const displayTitle =
      getEntryCategory(selectedEntry) === "micro_mission" &&
      selectedEntry.lesson_day_number &&
      selectedEntry.lesson_title
        ? `Día ${selectedEntry.lesson_day_number}: ${selectedEntry.lesson_title}`
        : getDisplayTitle(selectedEntry);
    const dateLabel = formatDate(
      selectedEntry.entry_date || selectedEntry.created_at,
    );

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(155, 117, 39);
    doc.text("ZEDRO ACADEMY", marginX, y);

    y += 18;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(102, 119, 132);
    doc.text("Formación con fundamento cristiano", marginX, y);

    y += 38;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(155, 117, 39);
    doc.text(
      `${selectedEntry.system_title || "Sistema ZEDRO Academy"} · Semana ${
        selectedEntry.week_number || "—"
      }`,
      marginX,
      y,
    );

    y += 18;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(15, 47, 70);
    doc.text(categoryLabel, marginX, y);

    y += 30;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(22);
    doc.setTextColor(15, 47, 70);

    const titleLines = doc.splitTextToSize(displayTitle, contentWidth);
    titleLines.forEach((line) => {
      doc.text(line, marginX, y);
      y += 27;
    });

    y += 8;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(102, 119, 132);
    doc.text(`Fecha: ${dateLabel}`, marginX, y);

    y += 32;

    const journalEntries = selectedEntry.response?.journal_entries || [];

    journalEntries.forEach((item) => {
      addSectionTitle(getBlockLabel(item.kind));

      if (item.question) {
        addWrappedText(item.question, {
          fontSize: 11,
          fontStyle: "bold",
          color: [15, 47, 70],
          lineHeight: 16,
          spacingAfter: 8,
        });
      }

      if (item.answer) {
        addWrappedText(item.answer, {
          fontSize: 10.5,
          color: [34, 56, 77],
          lineHeight: 15,
          spacingAfter: 18,
        });
      }
    });

    const personalNote = isConnectionEntry
      ? ""
      : cleanPdfText(selectedEntry.personal_note);

    if (personalNote) {
      addSectionTitle("Notas personales");

      addWrappedText(personalNote, {
        fontSize: 10.5,
        color: [34, 56, 77],
        lineHeight: 15,
        spacingAfter: 18,
      });
    }

    footer();

    doc.save(buildPdfFileName(selectedEntry));
  }

  return (
    <main className="bitacoraPage">
      <section className="bitacoraShell">
        <button
          type="button"
          onClick={() => navigate("/dashboard")}
          className="z-back-pill bitacoraBackControl"
        >
          ← Volver al panel central
        </button>

        <header className="bitacoraHeader">
          <div className="bitacoraEyebrow">Workbook personal</div>

          <h1>Bitácora ZEDRO</h1>

          <p>
            Tu biblioteca personal para volver a lo aprendido, revisar tus
            decisiones y consultar la evidencia de tu avance en cada recorrido
            de ZEDRO Academy.
          </p>
        </header>

        <section className="bitacoraExplorer" ref={resultsRef}>
          <nav
            className="bitacoraModuleTabs"
            aria-label="Módulos de la Bitácora"
            role="tablist"
          >
            {moduleTabs.map((module) => {
              const active = activeModule === module.id;

              return (
                <button
                  key={module.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  className={active ? "is-active" : ""}
                  onClick={() => handleModuleChange(module.id)}
                >
                  <span>{module.label}</span>
                  <strong>{module.count}</strong>
                </button>
              );
            })}
          </nav>

          <div className="bitacoraFilterBar">
            <label className="bitacoraField bitacoraSearchField">
              <span>Buscar en mi Bitácora</span>
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar por palabra, decisión o tema..."
              />
            </label>

            <div className="bitacoraDateRange" aria-label="Filtrar por fecha">
              <label className="bitacoraField bitacoraDateField">
                <span>Desde</span>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(event) => setDateFrom(event.target.value)}
                />
              </label>

              <label className="bitacoraField bitacoraDateField">
                <span>Hasta</span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(event) => setDateTo(event.target.value)}
                />
              </label>
            </div>

            <button
              type="button"
              onClick={clearFilters}
              className="bitacoraResetButton"
              disabled={!hasActiveFilters}
            >
              Restablecer
            </button>
          </div>

          <div className="bitacoraResultsHeader">
            <div>
              <span>Biblioteca personal</span>
              <h2>
                {loading
                  ? "Cargando registros..."
                  : `${filteredEntries.length} ${
                      filteredEntries.length === 1 ? "registro" : "registros"
                    } encontrados`}
              </h2>
            </div>

            {!loading && filteredEntries.length > 0 && (
              <p>
                Página {currentPage} de {totalPages}
              </p>
            )}
          </div>

          {loading && (
            <div className="bitacoraStatusPanel">
              <p>Cargando Bitácora...</p>
            </div>
          )}

          {!loading && filteredEntries.length === 0 && (
            <div className="bitacoraStatusPanel">
              <div className="bitacoraEyebrow">Sin resultados</div>
              <h3>No hay registros para esta búsqueda</h3>
              <p>
                Cambia el módulo, ajusta las fechas o restablece los filtros
                para volver a consultar tu biblioteca.
              </p>
            </div>
          )}

          {!loading && paginatedEntries.length > 0 && (
            <div className="bitacoraResultsGrid">
              {paginatedEntries.map((entry) => {
                const active = selectedEntryId === entry.id;
                const module = getEntryModule(entry);

                return (
                  <button
                    key={entry.id}
                    type="button"
                    onClick={() => handleSelectEntry(entry.id)}
                    className={`bitacoraResultCard ${
                      active ? "is-active" : ""
                    }`}
                    aria-pressed={active}
                  >
                    <div className="bitacoraResultCardTop">
                      <span className="bitacoraResultModule">
                        {module.label}
                      </span>
                      <span className="bitacoraResultType">
                        {getEntryCategoryLabel(entry)}
                      </span>
                    </div>

                    <h3>{getDisplayTitle(entry)}</h3>

                    <div className="bitacoraResultCardBottom">
                      <time>
                        {formatDate(entry.entry_date || entry.created_at)}
                      </time>
                      <span>Ver registro →</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {!loading && totalPages > 1 && (
            <nav className="bitacoraPagination" aria-label="Paginación">
              <button
                type="button"
                onClick={() => changePage(currentPage - 1)}
                disabled={currentPage === 1}
                className="bitacoraPaginationArrow"
                aria-label="Página anterior"
              >
                ←
              </button>

              <div className="bitacoraPaginationPages">
                {paginationItems.map((item) =>
                  typeof item === "number" ? (
                    <button
                      key={item}
                      type="button"
                      onClick={() => changePage(item)}
                      className={item === currentPage ? "is-active" : ""}
                      aria-current={item === currentPage ? "page" : undefined}
                    >
                      {item}
                    </button>
                  ) : (
                    <span key={item} aria-hidden="true">
                      …
                    </span>
                  ),
                )}
              </div>

              <button
                type="button"
                onClick={() => changePage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="bitacoraPaginationArrow"
                aria-label="Página siguiente"
              >
                →
              </button>
            </nav>
          )}
        </section>
      </section>

      {readerOpen && selectedEntry && (
        <div
          className="bitacoraReaderOverlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="bitacora-reader-title"
        >
          <button
            type="button"
            className="bitacoraReaderBackdrop"
            onClick={closeReader}
            aria-label="Cerrar registro"
          />

          <section className="bitacoraReaderPanel">
            <div className="bitacoraReaderBar">
              <button type="button" onClick={closeReader}>
                ← Volver a la Bitácora
              </button>

              <span>{getEntryModule(selectedEntry).label}</span>
            </div>

            <div className="bitacoraReaderScroll">
              <article className="bitacoraDetailCard">
                <div className="bitacoraDetailTop">
                  <div>
                    <div className="bitacoraDetailMeta">
                      {selectedEntry.system_title || "ZEDRO"} · Semana{" "}
                      {selectedEntry.week_number || "—"}
                    </div>

                    <div className="bitacoraDetailCategory">
                      {getEntryCategoryLabel(selectedEntry)}
                    </div>

                    <h2 id="bitacora-reader-title">
                      {getDisplayTitle(selectedEntry)}
                    </h2>

                    {selectedEntry?.prompt && !isConnectionWallSnapshot && (
                      <p className="bitacoraPrompt">{selectedEntry.prompt}</p>
                    )}

                    {isConnectionWallSnapshot && (
                      <p className="bitacoraWallIntroText">
                        Lleva el seguimiento diario de tu Muro de Oración con
                        los registros bíblicos, peticiones, oraciones y
                        victorias diarias registradas en Bitácora.
                      </p>
                    )}

                    {selectedEntry.lesson_title &&
                      !["word", "micro_mission"].includes(
                        getEntryCategory(selectedEntry),
                      ) && (
                        <p className="bitacoraLesson">
                          Día {selectedEntry.lesson_day_number}:{" "}
                          {selectedEntry.lesson_title}
                        </p>
                      )}
                  </div>

                  <div className="bitacoraDetailDate">
                    <span>
                      {formatDate(
                        selectedEntry.entry_date || selectedEntry.created_at,
                      )}
                    </span>
                  </div>
                </div>

                <div className="bitacoraDownloadRow">
                  <button
                    type="button"
                    onClick={downloadSelectedEntryPdf}
                    className="bitacoraDownloadButton"
                  >
                    Descargar PDF
                  </button>
                  {(
                    Array.isArray(selectedEntry.response?.reflection_cards) &&
                    selectedEntry.response.reflection_cards.length
                      ? selectedEntry.response.reflection_cards
                      : selectedEntry.response?.reflection_card
                        ? [selectedEntry.response.reflection_card]
                        : []
                  ).map((card, index, cards) => (
                    <React.Fragment key={`${card.title}-${index}`}>
                      <button
                        type="button"
                        onClick={() => downloadReflectionCard("vertical", card)}
                        className="bitacoraDownloadButton"
                      >
                        {cards.length > 1
                          ? `Tarjeta ${index + 1}`
                          : "Descargar tarjeta"}
                      </button>
                      <button
                        type="button"
                        onClick={() => downloadReflectionCard("wallpaper", card)}
                        className="bitacoraDownloadButton"
                      >
                        {cards.length > 1
                          ? `Wallpaper ${index + 1}`
                          : "Descargar wallpaper"}
                      </button>
                    </React.Fragment>
                  ))}
                </div>

                {renderResponse(selectedEntry.response, selectedEntry)}

                {!isConnectionEntry && (
                  <section className="bitacoraPersonalNotes">
                    <div className="bitacoraPersonalNotesHeader">
                      <div>
                        <span>Notas personales</span>
                        <h3>Mis apuntes sobre este registro</h3>
                      </div>
                    </div>

                    <textarea
                      value={noteDraft}
                      onChange={(event) => setNoteDraft(event.target.value)}
                      placeholder="Escribe aquí tus notas personales sobre este día, esta micro-misión o esta Palabra trabajada..."
                    />

                    <div className="bitacoraPersonalNotesActions">
                      <button
                        type="button"
                        onClick={savePersonalNote}
                        disabled={savingNote}
                      >
                        {savingNote ? "Guardando..." : "Guardar nota"}
                      </button>

                      {noteMessage && <p>{noteMessage}</p>}
                    </div>
                  </section>
                )}
              </article>
            </div>
          </section>
        </div>
      )}

      <style>
        {`
          .bitacoraPage {
            width: 100%;
            max-width: 100%;
            min-height: 100vh;
            overflow-x: hidden;
            background: var(--page-bg);
            padding: 2rem 1.25rem 4rem;
            box-sizing: border-box;
          }

          .bitacoraPage *,
          .bitacoraPage *::before,
          .bitacoraPage *::after {
            box-sizing: border-box;
          }

          .bitacoraShell {
            width: 100%;
            max-width: 1240px;
            margin: 0 auto;
          }

          .bitacoraBackControl {
            margin-bottom: 1.25rem;
          }

          .bitacoraHeader {
            margin-bottom: 1.8rem;
            padding-top: 0.25rem;
          }

          .bitacoraEyebrow {
            display: inline-flex;
            margin-bottom: 0.8rem;
            padding: 0.38rem 0.75rem;
            border-radius: 999px;
            background: rgba(204, 163, 82, 0.12);
            color: #9b7527;
            font-size: 0.72rem;
            font-weight: 800;
            letter-spacing: 0.12em;
            text-transform: uppercase;
          }

          .bitacoraHeader h1 {
            margin: 0;
            color: #0f2f46;
            font-size: clamp(2rem, 4vw, 3rem);
            font-weight: 400;
            letter-spacing: -0.04em;
            line-height: 1.05;
          }

          .bitacoraHeader p {
            margin: 0.9rem 0 0;
            max-width: 790px;
            color: rgba(15, 47, 70, 0.68);
            font-size: 1rem;
            line-height: 1.65;
          }

          .bitacoraExplorer {
            margin-bottom: 1.35rem;
            border-radius: 30px;
            background: #ffffff;
            border: 1px solid rgba(15, 47, 70, 0.08);
            box-shadow: 0 18px 45px rgba(15, 47, 70, 0.06);
            overflow: hidden;
            scroll-margin-top: 100px;
          }

          .bitacoraModuleTabs {
            display: flex;
            align-items: center;
            gap: 0.25rem;
            min-width: 0;
            padding: 0 1.1rem;
            overflow-x: auto;
            overflow-y: hidden;
            border-bottom: 1px solid rgba(15, 47, 70, 0.08);
            scrollbar-width: thin;
            scrollbar-color: rgba(204, 163, 82, 0.42) transparent;
          }

          .bitacoraModuleTabs button {
            position: relative;
            flex: 0 0 auto;
            min-height: 64px;
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0 0.8rem;
            border: 0;
            background: transparent;
            color: rgba(15, 47, 70, 0.62);
            font-family: Questrial, sans-serif;
            font-size: 0.94rem;
            cursor: pointer;
            white-space: nowrap;
          }

          .bitacoraModuleTabs button::after {
            content: "";
            position: absolute;
            left: 0.8rem;
            right: 0.8rem;
            bottom: 0;
            height: 3px;
            border-radius: 999px 999px 0 0;
            background: transparent;
          }

          .bitacoraModuleTabs button strong {
            min-width: 24px;
            height: 24px;
            padding: 0 0.38rem;
            border-radius: 999px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            background: rgba(15, 47, 70, 0.05);
            color: rgba(15, 47, 70, 0.58);
            font-size: 0.72rem;
          }

          .bitacoraModuleTabs button.is-active {
            color: #0f2f46;
            font-weight: 800;
          }

          .bitacoraModuleTabs button.is-active::after {
            background: #cca352;
          }

          .bitacoraModuleTabs button.is-active strong {
            background: rgba(204, 163, 82, 0.16);
            color: #8a692c;
          }

          .bitacoraFilterBar {
            display: grid;
            grid-template-columns: minmax(280px, 1fr) auto auto;
            gap: 0.85rem;
            align-items: end;
            padding: 1.1rem;
            background: linear-gradient(
              180deg,
              rgba(246, 248, 249, 0.92),
              rgba(255, 255, 255, 0.98)
            );
            border-bottom: 1px solid rgba(15, 47, 70, 0.07);
          }

          .bitacoraField {
            min-width: 0;
            display: grid;
            gap: 0.38rem;
          }

          .bitacoraField span {
            color: rgba(15, 47, 70, 0.62);
            font-size: 0.74rem;
            font-weight: 800;
          }

          .bitacoraField input {
            width: 100%;
            min-width: 0;
            height: 46px;
            padding: 0 0.82rem;
            border-radius: 14px;
            border: 1px solid rgba(15, 47, 70, 0.12);
            outline: none;
            color: #0f2f46;
            background: #ffffff;
            font-family: Questrial, sans-serif;
            font-size: 0.92rem;
          }

          .bitacoraField input:focus {
            border-color: rgba(204, 163, 82, 0.52);
            box-shadow: 0 0 0 4px rgba(204, 163, 82, 0.08);
          }

          .bitacoraDateRange {
            display: grid;
            grid-template-columns: repeat(2, minmax(150px, 1fr));
            gap: 0.65rem;
          }

          .bitacoraResetButton {
            min-height: 46px;
            padding: 0 0.95rem;
            border-radius: 14px;
            border: 1px solid rgba(204, 163, 82, 0.3);
            background: #ffffff;
            color: #7b6430;
            font-family: Questrial, sans-serif;
            font-size: 0.86rem;
            font-weight: 800;
            cursor: pointer;
            white-space: nowrap;
          }

          .bitacoraResetButton:hover:not(:disabled) {
            background: rgba(204, 163, 82, 0.08);
            border-color: rgba(204, 163, 82, 0.46);
          }

          .bitacoraResetButton:disabled {
            opacity: 0.42;
            cursor: default;
          }

          .bitacoraResultsHeader {
            display: flex;
            align-items: flex-end;
            justify-content: space-between;
            gap: 1rem;
            padding: 1.25rem 1.25rem 0.9rem;
          }

          .bitacoraResultsHeader span {
            display: block;
            margin-bottom: 0.28rem;
            color: #9b7527;
            font-size: 0.7rem;
            font-weight: 900;
            letter-spacing: 0.12em;
            text-transform: uppercase;
          }

          .bitacoraResultsHeader h2 {
            margin: 0;
            color: #0f2f46;
            font-size: 1.22rem;
            font-weight: 500;
          }

          .bitacoraResultsHeader p {
            margin: 0;
            color: rgba(15, 47, 70, 0.52);
            font-size: 0.84rem;
          }

          .bitacoraResultsGrid {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 0.85rem;
            padding: 0 1.25rem 1.25rem;
          }

          .bitacoraResultCard {
            width: 100%;
            min-height: 178px;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            gap: 0.9rem;
            padding: 1rem;
            border-radius: 20px;
            border: 1px solid rgba(15, 47, 70, 0.08);
            background: linear-gradient(
              160deg,
              rgba(250, 251, 251, 0.98),
              rgba(245, 248, 249, 0.9)
            );
            color: inherit;
            text-align: left;
            font-family: Questrial, sans-serif;
            cursor: pointer;
            transition:
              transform 0.18s ease,
              border-color 0.18s ease,
              box-shadow 0.18s ease,
              background 0.18s ease;
          }

          .bitacoraResultCard:hover {
            transform: translateY(-2px);
            border-color: rgba(204, 163, 82, 0.34);
            box-shadow: 0 14px 30px rgba(15, 47, 70, 0.08);
          }

          .bitacoraResultCard.is-active {
            border-color: rgba(204, 163, 82, 0.58);
            background: linear-gradient(
              160deg,
              rgba(255, 252, 245, 1),
              rgba(255, 255, 255, 0.98)
            );
            box-shadow:
              0 0 0 2px rgba(204, 163, 82, 0.08),
              0 16px 34px rgba(15, 47, 70, 0.09);
          }

          .bitacoraResultCardTop {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 0.65rem;
          }

          .bitacoraResultModule,
          .bitacoraResultType {
            font-size: 0.66rem;
            font-weight: 900;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            line-height: 1.3;
          }

          .bitacoraResultModule {
            color: #9b7527;
          }

          .bitacoraResultType {
            color: rgba(15, 47, 70, 0.48);
            text-align: right;
          }

          .bitacoraResultCard h3 {
            margin: 0;
            color: #0f2f46;
            font-size: 1.06rem;
            font-weight: 700;
            line-height: 1.32;
          }

          .bitacoraResultCardBottom {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 0.65rem;
            padding-top: 0.75rem;
            border-top: 1px solid rgba(15, 47, 70, 0.07);
          }

          .bitacoraResultCardBottom time {
            color: rgba(15, 47, 70, 0.5);
            font-size: 0.78rem;
          }

          .bitacoraResultCardBottom span {
            color: #8a692c;
            font-size: 0.78rem;
            font-weight: 900;
            white-space: nowrap;
          }

          .bitacoraStatusPanel {
            margin: 0 1.25rem 1.25rem;
            padding: 1.4rem;
            border-radius: 20px;
            background: rgba(15, 47, 70, 0.035);
            border: 1px solid rgba(15, 47, 70, 0.07);
          }

          .bitacoraStatusPanel h3 {
            margin: 0;
            color: #0f2f46;
            font-size: 1.18rem;
          }

          .bitacoraStatusPanel p {
            margin: 0.55rem 0 0;
            max-width: 580px;
            color: rgba(15, 47, 70, 0.62);
            line-height: 1.55;
          }

          .bitacoraPagination {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.65rem;
            padding: 0 1.25rem 1.3rem;
          }

          .bitacoraPagination button {
            min-width: 40px;
            height: 40px;
            border-radius: 12px;
            border: 1px solid rgba(15, 47, 70, 0.1);
            background: #ffffff;
            color: #0f2f46;
            font-family: Questrial, sans-serif;
            font-weight: 800;
            cursor: pointer;
          }

          .bitacoraPagination button:hover:not(:disabled) {
            border-color: rgba(204, 163, 82, 0.42);
            background: rgba(204, 163, 82, 0.08);
          }

          .bitacoraPagination button:disabled {
            opacity: 0.35;
            cursor: default;
          }

          .bitacoraPaginationPages {
            display: flex;
            align-items: center;
            gap: 0.38rem;
          }

          .bitacoraPaginationPages button.is-active {
            border-color: #cca352;
            background: #cca352;
            color: #102c40;
          }

          .bitacoraPaginationPages span {
            min-width: 28px;
            text-align: center;
            color: rgba(15, 47, 70, 0.48);
          }

          .bitacoraReaderOverlay {
            position: fixed;
            inset: 0;
            z-index: 20000;
            display: grid;
            place-items: center;
            padding: 1.25rem;
          }

          .bitacoraReaderBackdrop {
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
            border: 0;
            background: rgba(7, 20, 31, 0.76);
            backdrop-filter: blur(10px);
            cursor: pointer;
          }

          .bitacoraReaderPanel {
            position: relative;
            z-index: 1;
            width: min(1180px, calc(100vw - 2.5rem));
            height: min(92dvh, 900px);
            min-height: 0;
            display: flex;
            flex-direction: column;
            overflow: hidden;
            border-radius: 30px;
            background: #ffffff;
            border: 1px solid rgba(204, 163, 82, 0.24);
            box-shadow: 0 34px 100px rgba(0, 0, 0, 0.34);
          }

          .bitacoraReaderBar {
            flex: 0 0 auto;
            min-height: 64px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 1rem;
            padding: 0.7rem 1rem;
            border-bottom: 1px solid rgba(15, 47, 70, 0.09);
            background: rgba(255, 255, 255, 0.98);
          }

          .bitacoraReaderBar button {
            min-height: 42px;
            padding: 0 0.95rem;
            border-radius: 999px;
            border: 1px solid rgba(15, 47, 70, 0.12);
            background: #ffffff;
            color: #0f2f46;
            font-family: Questrial, sans-serif;
            font-size: 0.9rem;
            font-weight: 800;
            cursor: pointer;
          }

          .bitacoraReaderBar button:hover {
            border-color: rgba(204, 163, 82, 0.42);
            background: rgba(204, 163, 82, 0.08);
          }

          .bitacoraReaderBar span {
            color: #9b7527;
            font-size: 0.72rem;
            font-weight: 900;
            letter-spacing: 0.1em;
            line-height: 1.25;
            text-align: right;
            text-transform: uppercase;
          }

          .bitacoraReaderScroll {
            flex: 1 1 auto;
            min-height: 0;
            overflow-y: auto;
            overscroll-behavior: contain;
            scrollbar-width: thin;
            scrollbar-color: rgba(204, 163, 82, 0.48) transparent;
          }

          .bitacoraReaderPanel .bitacoraDetailCard {
            min-height: 100%;
            padding: clamp(1.35rem, 3vw, 2.25rem);
            border: 0;
            border-radius: 0;
            box-shadow: none;
          }

          .bitacoraDetail {
            min-width: 0;
            scroll-margin-top: 96px;
          }

          .bitacoraDetailCard,
          .bitacoraEmptyPanel {
            width: 100%;
            padding: clamp(1.3rem, 3vw, 2rem);
            border-radius: 30px;
            background: #ffffff;
            border: 1px solid rgba(15, 47, 70, 0.08);
            box-shadow: 0 18px 45px rgba(15, 47, 70, 0.06);
          }

          .bitacoraEmptyPanel h2 {
            margin: 0;
            color: #0f2f46;
            font-weight: 500;
          }

          .bitacoraEmptyPanel p {
            max-width: 520px;
            color: rgba(15, 47, 70, 0.68);
            line-height: 1.65;
          }

          .bitacoraDetailTop {
            display: flex;
            justify-content: space-between;
            gap: 1rem;
            flex-wrap: wrap;
            margin-bottom: 1.2rem;
          }

          .bitacoraDetailMeta,
          .bitacoraDetailCategory {
            color: #9b7527;
            font-size: 0.72rem;
            font-weight: 800;
            letter-spacing: 0.1em;
            text-transform: uppercase;
          }

          .bitacoraDetailCategory {
            margin-top: 0.35rem;
            color: rgba(15, 47, 70, 0.5);
          }

          .bitacoraDetailCard h2 {
            margin: 0.45rem 0 0;
            color: #0f2f46;
            font-size: clamp(1.45rem, 3vw, 2.2rem);
            font-weight: 400;
            letter-spacing: -0.035em;
            line-height: 1.1;
          }

          .bitacoraLesson {
            margin: 0.45rem 0 0;
            color: rgba(15, 47, 70, 0.62);
            line-height: 1.55;
          }

          .bitacoraDetailDate {
            text-align: right;
            color: rgba(15, 47, 70, 0.62);
            font-size: 0.9rem;
          }

          .bitacoraDetailDate span,
          .bitacoraDetailDate strong {
            display: block;
          }

          .bitacoraDetailDate strong {
            margin-top: 0.35rem;
            color: #0f2f46;
          }

          .bitacoraPrompt {
            margin: 0.7rem 0 0;
            color: rgba(15, 47, 70, 0.72);
            line-height: 1.65;
          }

          .bitacoraReflectionCard {
            position: relative;
            margin: 0 0 1rem;
            overflow: hidden;
            padding: clamp(1.5rem, 4vw, 2.4rem);
            border-radius: 26px;
            color: #ffffff;
            background:
              radial-gradient(circle at 88% 8%, rgba(213, 180, 106, 0.25), transparent 32%),
              linear-gradient(135deg, #173449, #264763 62%, #385c75);
            box-shadow: 0 18px 38px rgba(23, 52, 73, 0.16);
          }

          .bitacoraReflectionCard > span {
            display: block;
            margin-bottom: 0.65rem;
            color: #e5c985;
            font-size: 0.72rem;
            font-weight: 800;
            letter-spacing: 0.12em;
            text-transform: uppercase;
          }

          .bitacoraReflectionCard h3 {
            margin: 0;
            max-width: 700px;
            color: #ffffff;
            font-family: Georgia, "Times New Roman", serif;
            font-size: clamp(1.45rem, 3vw, 2rem);
            font-weight: 500;
          }

          .bitacoraReflectionCard p {
            max-width: 760px;
            margin: 1rem 0 0;
            color: rgba(255, 255, 255, 0.9);
            line-height: 1.7;
            white-space: pre-line;
          }

          .bitacoraResponseGrid {
            display: grid;
            gap: 1rem;
          }

          .bitacoraResponseCard {
            padding: 1rem;
            border-radius: 20px;
            background: rgba(15, 47, 70, 0.04);
            border: 1px solid rgba(15, 47, 70, 0.06);
          }

          .bitacoraResponseCard.is-rule {
            background: rgba(204, 163, 82, 0.08);
            border-color: rgba(204, 163, 82, 0.22);
          }

          .bitacoraResponseLabel {
            margin-bottom: 0.5rem;
            color: #9b7527;
            font-size: 0.74rem;
            font-weight: 800;
            letter-spacing: 0.08em;
            text-transform: uppercase;
          }

          .bitacoraResponseQuestion {
            margin: 0 0 0.75rem;
            color: #0f2f46;
            line-height: 1.55;
            font-weight: 600;
          }

          .bitacoraResponseAnswer {
            color: #22384d;
            line-height: 1.6;
            font-size: 0.96rem;
            background: #ffffff;
            border-radius: 14px;
            padding: 0.85rem 1rem;
            white-space: normal;
            overflow-wrap: anywhere;
          }

          .bitacoraEmptyText {
            color: rgba(15, 47, 70, 0.62);
            line-height: 1.55;
          }

          .bitacoraPersonalNotes {
            margin-top: 1.25rem;
            padding: 1.15rem;
            border-radius: 22px;
            background: linear-gradient(
              180deg,
              rgba(204, 163, 82, 0.08),
              rgba(255, 255, 255, 0.95)
            );
            border: 1px solid rgba(204, 163, 82, 0.22);
          }

          .bitacoraPersonalNotesHeader {
            display: flex;
            justify-content: space-between;
            gap: 1rem;
            margin-bottom: 0.85rem;
          }

          .bitacoraPersonalNotesHeader span {
            display: block;
            margin-bottom: 0.25rem;
            color: #9b7527;
            font-size: 0.72rem;
            font-weight: 900;
            letter-spacing: 0.1em;
            text-transform: uppercase;
          }

          .bitacoraPersonalNotesHeader h3 {
            margin: 0;
            color: #0f2f46;
            font-size: 1.15rem;
            font-weight: 500;
          }

          .bitacoraPersonalNotes textarea {
            width: 100%;
            min-height: 150px;
            resize: vertical;
            padding: 1rem;
            border-radius: 18px;
            border: 1px solid rgba(15, 47, 70, 0.12);
            background: #ffffff;
            color: #22384d;
            font-family: Questrial, sans-serif;
            font-size: 1rem;
            line-height: 1.65;
            outline: none;
          }

          .bitacoraPersonalNotes textarea:focus {
            border-color: rgba(204, 163, 82, 0.42);
            box-shadow: 0 0 0 4px rgba(204, 163, 82, 0.08);
          }

          .bitacoraPersonalNotesActions {
            display: flex;
            align-items: center;
            gap: 0.9rem;
            flex-wrap: wrap;
            margin-top: 0.85rem;
          }

          .bitacoraPersonalNotesActions button {
            min-height: 42px;
            padding: 0 1.05rem;
            border-radius: 999px;
            border: 1px solid rgba(204, 163, 82, 0.34);
            background: #ffffff;
            color: #7b6430;
            font-family: Questrial, sans-serif;
            font-weight: 900;
            cursor: pointer;
          }

          .bitacoraPersonalNotesActions button:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          .bitacoraPersonalNotesActions p {
            margin: 0;
            color: rgba(15, 47, 70, 0.62);
            font-size: 0.9rem;
          }

          .bitacoraDownloadRow {
            display: flex;
            justify-content: flex-end;
            margin: 1rem 0 1.2rem;
          }

          .bitacoraDownloadButton {
            min-height: 42px;
            padding: 0 1.1rem;
            border-radius: 999px;
            border: 1px solid rgba(204, 163, 82, 0.38);
            background: linear-gradient(
              180deg,
              rgba(204, 163, 82, 0.12),
              #ffffff
            );
            color: #7b6430;
            font-family: Questrial, sans-serif;
            font-size: 0.94rem;
            font-weight: 900;
            cursor: pointer;
            box-shadow: 0 10px 24px rgba(15, 47, 70, 0.04);
          }

          .bitacoraDownloadButton:hover {
            border-color: rgba(204, 163, 82, 0.52);
            box-shadow: 0 14px 30px rgba(15, 47, 70, 0.08);
          }

          .bitacoraWallSnapshot {
            border-radius: 30px;
            border: 1px solid rgba(204, 163, 82, 0.32);
            background:
              radial-gradient(
                circle at 24% 10%,
                rgba(227, 197, 112, 0.18),
                transparent 18%
              ),
              radial-gradient(
                circle at 78% 12%,
                rgba(58, 89, 120, 0.14),
                transparent 18%
              ),
              linear-gradient(
                135deg,
                #0c263a 0%,
                #13354d 42%,
                #0f2f46 72%,
                #081f31 100%
              );
            padding: 1.1rem;
            box-shadow: 0 22px 55px rgba(6, 18, 28, 0.16);
          }

          .bitacoraWallSnapshotHead {
            margin-bottom: 0.85rem;
          }

          .bitacoraWallSnapshotHead span {
            display: inline-flex;
            margin-bottom: 0.55rem;
            padding: 0.4rem 0.75rem;
            border-radius: 999px;
            background: rgba(255, 248, 232, 0.14);
            border: 1px solid rgba(204, 163, 82, 0.28);
            color: #e3c570;
            font-size: 0.72rem;
            font-weight: 900;
            letter-spacing: 0.11em;
            text-transform: uppercase;
          }

          .bitacoraWallGrid {
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 0.85rem;
          }

          .bitacoraWallColumn {
            min-height: 320px;
            padding: 0.7rem;
            border-radius: 22px;
            border: 1px solid rgba(204, 163, 82, 0.34);
            background:
              radial-gradient(
                circle at top left,
                rgba(204, 163, 82, 0.13),
                transparent 38%
              ),
              linear-gradient(
                180deg,
                rgba(244, 239, 225, 0.96),
                rgba(255, 255, 255, 0.78)
              );
          }

          .bitacoraWallColumn--petitions {
            background:
              radial-gradient(
                circle at top left,
                rgba(15, 47, 70, 0.11),
                transparent 40%
              ),
              linear-gradient(
                180deg,
                rgba(226, 236, 240, 0.96),
                rgba(255, 255, 255, 0.78)
              );
          }

          .bitacoraWallColumn--prayers {
            background:
              radial-gradient(
                circle at top left,
                rgba(204, 163, 82, 0.12),
                transparent 38%
              ),
              linear-gradient(
                180deg,
                rgba(239, 234, 220, 0.98),
                rgba(255, 255, 255, 0.78)
              );
          }

          .bitacoraWallColumn--victories {
            background:
              radial-gradient(
                circle at top left,
                rgba(39, 91, 76, 0.12),
                transparent 40%
              ),
              linear-gradient(
                180deg,
                rgba(226, 239, 232, 0.96),
                rgba(255, 255, 255, 0.78)
              );
          }

          .bitacoraWallColumnHead h4 {
            margin: 0;
            color: #0f2f46;
            font-size: 0.98rem;
            font-weight: 900;
          }

          .bitacoraWallColumnHead p {
            margin: 0.28rem 0 0.75rem;
            color: rgba(15, 47, 70, 0.58);
            font-size: 0.8rem;
            line-height: 1.4;
          }

          .bitacoraWallCards {
            display: grid;
            gap: 0.7rem;
          }

          .bitacoraWallCard {
            padding: 0.72rem;
            border-radius: 16px;
            background: rgba(255, 255, 255, 0.86);
            border: 1px solid rgba(15, 47, 70, 0.08);
            box-shadow: 0 10px 22px rgba(15, 47, 70, 0.05);
          }

          .bitacoraWallCardTop {
            display: flex;
            justify-content: space-between;
            gap: 0.45rem;
            margin-bottom: 0.45rem;
            color: #9b7527;
            font-size: 0.62rem;
            font-weight: 900;
            letter-spacing: 0.06em;
            line-height: 1.25;
            text-transform: uppercase;
          }

          .bitacoraWallCard h4 {
            margin: 0 0 0.45rem;
            color: #0f2f46;
            font-size: 0.94rem;
            font-weight: 900;
            line-height: 1.3;
          }

          .bitacoraWallReference {
            margin: 0 0 0.45rem;
            color: #8a692c;
            font-size: 0.86rem;
            font-weight: 900;
          }

          .bitacoraWallText,
          .bitacoraWallPrayer p,
          .bitacoraWallVictory p {
            margin: 0;
            color: rgba(15, 47, 70, 0.75);
            font-size: 0.82rem;
            line-height: 1.48;
          }

          .bitacoraWallPrayer,
          .bitacoraWallVictory {
            margin-top: 0.65rem;
            padding: 0.7rem;
            border-radius: 14px;
            background: rgba(15, 47, 70, 0.04);
          }

          .bitacoraWallPrayer strong,
          .bitacoraWallVictory strong {
            display: block;
            margin-bottom: 0.35rem;
            color: #0f2f46;
            font-size: 0.78rem;
            font-weight: 900;
          }

          .bitacoraWallEmpty {
            margin: 0;
            color: rgba(15, 47, 70, 0.56);
            font-size: 0.84rem;
            line-height: 1.5;
          }

          .bitacoraWallIntroText {
            max-width: 620px;
            margin: 0.75rem 0 0;
            color: rgba(15, 47, 70, 0.68);
            font-size: 0.98rem;
            line-height: 1.6;
          }

          @media (max-width: 1000px) {
            .bitacoraFilterBar {
              grid-template-columns: minmax(0, 1fr) auto;
            }

            .bitacoraSearchField {
              grid-column: 1 / -1;
            }

            .bitacoraResultsGrid {
              grid-template-columns: repeat(2, minmax(0, 1fr));
            }

            .bitacoraWallGrid {
              grid-template-columns: repeat(2, minmax(0, 1fr));
            }
          }

          @media (max-width: 720px) {
            .bitacoraPage {
              padding: 1.2rem 0.85rem 3rem;
            }

            .bitacoraHeader h1 {
              font-size: clamp(2.2rem, 10vw, 3rem);
            }

            .bitacoraHeader p {
              font-size: 1rem;
              line-height: 1.7;
            }

            .bitacoraExplorer {
              border-radius: 24px;
            }

            .bitacoraModuleTabs {
              padding: 0 0.55rem;
              scroll-snap-type: x proximity;
            }

            .bitacoraModuleTabs button {
              min-height: 58px;
              padding: 0 0.68rem;
              scroll-snap-align: start;
            }

            .bitacoraFilterBar {
              grid-template-columns: 1fr;
              padding: 0.95rem;
            }

            .bitacoraSearchField {
              grid-column: auto;
            }

            .bitacoraDateRange {
              grid-template-columns: repeat(2, minmax(0, 1fr));
            }

            .bitacoraResetButton {
              width: fit-content;
              min-height: 38px;
              padding: 0 0.8rem;
              justify-self: end;
              border-radius: 999px;
              font-size: 0.82rem;
            }

            .bitacoraResultsHeader {
              align-items: flex-start;
              padding: 1.1rem 1rem 0.85rem;
            }

            .bitacoraResultsGrid {
              grid-template-columns: 1fr;
              padding: 0 1rem 1rem;
            }

            .bitacoraResultCard {
              min-height: 154px;
            }

            .bitacoraPagination {
              padding: 0 1rem 1.15rem;
            }

            .bitacoraPaginationPages {
              max-width: calc(100vw - 150px);
              overflow-x: auto;
            }

            .bitacoraDetailCard,
            .bitacoraEmptyPanel {
              border-radius: 24px;
              padding: 1.15rem;
            }

            .bitacoraDetailDate {
              text-align: left;
            }

            .bitacoraWallGrid {
              grid-template-columns: 1fr;
            }

            .bitacoraWallColumn {
              min-height: auto;
            }
          }

          @media (max-width: 640px) {
            .bitacoraReaderOverlay {
              padding: 0;
            }

            .bitacoraReaderPanel {
              width: 100vw;
              height: 100dvh;
              max-width: none;
              max-height: none;
              border: 0;
              border-radius: 0;
            }

            .bitacoraReaderBar {
              min-height: 60px;
              padding: 0.65rem 0.85rem;
            }

            .bitacoraReaderBar button {
              min-height: 42px;
              padding: 0 0.8rem;
            }

            .bitacoraReaderBar span {
              max-width: 42%;
              font-size: 0.66rem;
            }

            .bitacoraReaderPanel .bitacoraDetailCard {
              min-height: 100%;
              padding: 1rem 1rem 2.5rem;
            }

            .bitacoraReaderPanel .bitacoraDetailTop {
              display: block;
            }

            .bitacoraReaderPanel .bitacoraDetailCard h2 {
              font-size: clamp(1.85rem, 9vw, 2.45rem);
              line-height: 1.08;
            }

            .bitacoraReaderPanel .bitacoraDetailDate {
              margin-top: 0.9rem;
              display: flex;
              justify-content: space-between;
              gap: 0.75rem;
              text-align: left;
            }

            .bitacoraReaderPanel .bitacoraDetailDate strong {
              margin-top: 0;
            }

            .bitacoraReaderPanel .bitacoraDownloadRow {
              justify-content: stretch;
            }

            .bitacoraReaderPanel .bitacoraDownloadButton {
              width: 100%;
              min-height: 48px;
            }

            .bitacoraReaderPanel .bitacoraResponseGrid {
              gap: 0;
            }

            .bitacoraReaderPanel .bitacoraResponseCard {
              padding: 1.15rem 0;
              border: 0;
              border-bottom: 1px solid rgba(15, 47, 70, 0.09);
              border-radius: 0;
              background: transparent;
            }

            .bitacoraReaderPanel .bitacoraResponseCard.is-rule {
              margin: 0 -1rem;
              padding: 1.15rem 1rem;
              background: rgba(204, 163, 82, 0.08);
              border-top: 1px solid rgba(204, 163, 82, 0.18);
              border-bottom: 1px solid rgba(204, 163, 82, 0.18);
            }

            .bitacoraReaderPanel .bitacoraResponseQuestion {
              font-size: 1.05rem;
              line-height: 1.58;
            }

            .bitacoraReaderPanel .bitacoraResponseAnswer {
              padding: 0.9rem 0;
              border-radius: 0;
              background: transparent;
              font-size: 1rem;
              line-height: 1.66;
            }

            .bitacoraReaderPanel .bitacoraPersonalNotes {
              margin: 1.25rem -1rem 0;
              padding: 1.15rem 1rem;
              border-left: 0;
              border-right: 0;
              border-radius: 0;
            }

            .bitacoraReaderPanel .bitacoraPersonalNotes textarea {
              min-height: 180px;
            }

            .bitacoraReaderPanel .bitacoraWallSnapshot {
              margin: 0 -1rem;
              padding: 0.85rem;
              border-left: 0;
              border-right: 0;
              border-radius: 0;
            }
          }

          @media (max-width: 430px) {
            .bitacoraPage {
              padding-left: 0.7rem;
              padding-right: 0.7rem;
            }

            .bitacoraDateRange {
              grid-template-columns: 1fr;
            }

            .bitacoraResultsHeader {
              display: block;
            }

            .bitacoraResultsHeader p {
              margin-top: 0.45rem;
            }

            .bitacoraResultCardTop,
            .bitacoraResultCardBottom {
              align-items: flex-start;
            }

            .bitacoraResultCardBottom {
              flex-direction: column;
            }

            .bitacoraReaderBar span {
              display: none;
            }
          }
        `}
      </style>
    </main>
  );
}
